import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { handleUnauthorized } from "../../utils/apiHelper";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DangerAlert from "../child/Alert/DengerAlert"; // Assuming this is the correct name
import Loader from "../child/Loder";

const cleanInput = (value) => value.replace(/\s{2,}/g, " ").trimStart();

const AddPromotionPageNew = () => {
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");
  const [days, setDays] = useState("");
  const [selectedunit, setSelectedUnit] = useState("month");
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const authToken = localStorage.getItem("authToken");

  useEffect(() => {
    if (!isEditMode) return;

    const fetchSetting = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/admin/promotionPlanById/${id}`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );

        if (response.status === 401) {
          handleUnauthorized(navigate);
          return;
        }

        const data = await response.json();
        if (response.ok) {
          setPrice(data.result.price || "");
          setTitle(data.result.title || "");
          setDescription(data.result.description || "");
          setDays(data.result.duration.value || "");
          setSelectedUnit(data.result.duration.unit || "");
        } else {
          setErrorMessage("Error fetching Promotion");
          clearMessage();
        }
      } catch (error) {
        setErrorMessage("Error fetching Promotion");
        clearMessage();
      } finally {
        setLoading(false);
      }
    };

    fetchSetting();
  }, [id, navigate]);

  const clearMessage = () => {
    setTimeout(() => {
      setSuccessMessage("");
      setErrorMessage("");
    }, 3000);
  };

  const validateInputs = () => {
    const newErrors = {};
    if (!title) newErrors.title = "Title is required";
    if (!price) newErrors.price = "Price is required";
    if (!selectedunit) newErrors.selectedunit = "Please select unit";
    if (!description) newErrors.description = "Description is required";
    if (isNaN(Number(price))) newErrors.price = "Price must be a valid number";

    if (title) {
      const hasAlphabet = /[A-Za-z]/;
      if (!hasAlphabet.test(title)) {
        newErrors.title = "Title cannot be only numbers or special characters";
      }
      if (title.length < 3) {
        newErrors.title = "Title must be at least 3 characters long";
      }
    }

    if (description) {
      const hasAlphabet = /[A-Za-z]/;
      if (!hasAlphabet.test(description)) {
        newErrors.description =
          "Description cannot be only numbers or special characters";
      }
      if (description.length < 10) {
        newErrors.description =
          "Description must be at least 10 characters long";
      }
    }
    if (price && (Number(price) <= 0 || /^0\.[0-9]+$/.test(price))) {
      newErrors.price = "Price must be greater than 0";
    }

    if (!days) {
      newErrors.days =
        selectedunit === "month" ? "Please select month" : "Day is required";
    } else {
      const numericDays = Number(days);
      if (isNaN(numericDays) || numericDays <= 0) {
        newErrors.days = "Day value must be greater than 0";
      }
      if (selectedunit === "day" && numericDays > 28) {
        newErrors.days = "Day value must be less than or equal to 28";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    if (!validateInputs()) {
      setLoading(false);
      return;
    }

    try {
      const method = isEditMode ? "PUT" : "POST";
      const API_ENDPOINT = `${process.env.REACT_APP_BASE_URL}/admin/${
        isEditMode ? `promotionPlanUpdate/${id}` : "promotionPlanAdd"
      }`;
      const title1 = title.trimEnd();
      const duration = { value: parseInt(days, 10), unit: selectedunit };

      const response = await fetch(API_ENDPOINT, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          title: title1,
          description,
          price,
          duration,
          status: "active",
        }),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.message === "This promotion plan already exists.") {
          setErrorMessage(data.message);
        } else {
          setSuccessMessage(
            data.message ||
              (isEditMode ? "Promotion Updated" : "Promotion Added")
          );
          setTimeout(() => navigate("/promotion"), 3000);
        }
      } else {
        setErrorMessage(data.message);
      }
    } catch (error) {
      setErrorMessage(error?.message || "Something went wrong");
    } finally {
      setLoading(false);
      clearMessage();
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="row gy-4">
      <div className="col-lg-12">
        <div className="card">
          <div className="card-body">
            {successMessage && <SuccessAlert message={successMessage} />}
            {errorMessage && <DangerAlert message={errorMessage} />}
            <form
              className="row gy-3 needs-validation"
              noValidate
              onSubmit={handleSubmit}
            >
              <div className="col-md-12">
                <label className="form-label">
                  Title<span className="text-danger-600">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.title ? "is-invalid" : ""}`}
                  placeholder="Enter title"
                  value={title}
                  minLength="3"
                  maxLength="40"
                  onChange={(e) => {
                    setTitle(cleanInput(e.target.value));
                    setErrors((prev) => ({ ...prev, title: "" }));
                  }}
                  disabled={loading}
                />
                {errors.title && (
                  <span className="text-danger mt-1">{errors.title}</span>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label">
                  Select Unit<span className="text-danger-600">*</span>
                </label>
                <select
                  className={`form-control ${
                    errors.selectedunit ? "is-invalid" : ""
                  }`}
                  value={selectedunit}
                  onChange={(e) => {
                    setSelectedUnit(e.target.value);
                    setErrors((prev) => ({
                      ...prev,
                      selectedunit: "",
                      days: "",
                    }));
                    setDays("");
                  }}
                  disabled={loading}
                >
                  <option value="month">Month</option>
                  <option value="day">Day</option>
                </select>
                {errors.selectedunit && (
                  <span className="text-danger mt-1">
                    {errors.selectedunit}
                  </span>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label">
                  Enter unit value<span className="text-danger-600">*</span>
                </label>
                {selectedunit === "month" && (
                  <select
                    className={`form-control ${
                      errors.days ? "is-invalid" : ""
                    }`}
                    value={days}
                    onChange={(e) => {
                      setDays(e.target.value);
                      setErrors((prev) => ({ ...prev, days: "" }));
                    }}
                    disabled={loading}
                  >
                    <option value="">Select Month</option>
                    <option value="1">1</option>
                    <option value="3">3</option>
                    <option value="6">6</option>
                    <option value="9">9</option>
                  </select>
                )}
                {selectedunit === "day" && (
                  <input
                    type="text"
                    className={`form-control ${
                      errors.days ? "is-invalid" : ""
                    }`}
                    placeholder="Enter days"
                    maxLength="2"
                    value={days}
                    onChange={(e) => {
                      const numericValue = e.target.value.replace(
                        /[^0-9]/g,
                        ""
                      );
                      setDays(numericValue);
                      setErrors((prev) => ({ ...prev, days: "" }));
                    }}
                    disabled={loading}
                  />
                )}
                {errors.days && (
                  <span className="text-danger mt-1">{errors.days}</span>
                )}
              </div>

              <div className="col-md-12">
                <label className="form-label">
                  Price($)<span className="text-danger-600">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.price ? "is-invalid" : ""}`}
                  placeholder="Enter price"
                  value={price}
                  onChange={(e) => {
                    const numericValue = e.target.value.replace(/[^0-9.]/g, "");
                    setPrice(numericValue);
                    setErrors((prev) => ({ ...prev, price: "" }));
                  }}
                  disabled={loading}
                />
                {errors.price && (
                  <span className="text-danger mt-1">{errors.price}</span>
                )}
              </div>

              <div className="col-md-12">
                <label className="form-label">
                  Description<span className="text-danger-600">*</span>
                </label>
                <textarea
                  className={`form-control ${
                    errors.description ? "is-invalid" : ""
                  }`}
                  placeholder="Enter description"
                  value={description}
                  onChange={(e) => {
                    setDescription(cleanInput(e.target.value));
                    setErrors((prev) => ({ ...prev, description: "" }));
                  }}
                  disabled={loading}
                />
                {errors.description && (
                  <span className="text-danger mt-1">{errors.description}</span>
                )}
              </div>

              <div className="col-12">
                <button
                  className="btn btn-primary mt-3 float-end"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPromotionPageNew;
